import { useFinanceFunction } from "./use-finance-function";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { ChartNoAxesCombined } from "lucide-react";
import "../../features/eex/finance-workspace.css";
import { apiContract, selectedCompanyId } from "../../lib/api";
import { useMobileUiV1State } from "../../hooks/use-media-query";
import { cn } from "../../lib/utils";
import {
  defaultFinanceMonth,
  financeBaselineMonth,
  financeReportPath,
  financeReportMatchesBaseline,
  FinanceReportSettingsSchema,
  MonthlyFinanceReportSchema,
  type MonthlyFinanceReportData,
} from "../../features/eex/pnl/finance-report";
import { errMessage, Td, Th } from "./ui";
import { PortfolioMatrixWorkspace } from "./PortfolioMatrixWorkspace";
import { FinanceCarryRollforward } from "./FinanceCarryRollforward";
import { useAuth } from "../../stores/auth";
import { canReturnToFinance, type FinanceOrigin } from "../../features/eex/finance-navigation";

const reportTabs = [["finance", "Finance report"], ["matrix", "P&L by month"], ["carry", "P&L and carry"]] as const;

const number = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const price = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 6 });
const amount = (value: number | null) => value === null ? "—" : number.format(value);
const label = (value: string | null) => value ?? "—";

function Contract({ value }: { value: { code: string; expYear: number; expMonth: number } }) {
  return <>{value.code} · {value.expYear}-{String(value.expMonth).padStart(2, "0")}</>;
}

function Detail({ title, children }: { title: string; children: ReactNode }) {
  return (
    <details className="rounded border border-border">
      <summary className="min-h-11 cursor-pointer px-3 py-3 text-sm font-medium sm:min-h-0 sm:py-2">{title}</summary>
      <div className="space-y-3 px-3 pb-3">{children}</div>
    </details>
  );
}

function ScrollTable({ name, children }: { name: string; children: ReactNode }) {
  return (
    <div className="overflow-x-auto" role="region" aria-label={name} tabIndex={0}>
      <table className="w-full border-collapse text-xs">{children}</table>
    </div>
  );
}

async function downloadReport(month: string, companyId: string | null): Promise<void> {
  const response = await fetch(`/api${financeReportPath(month, "xlsx")}`, {
    credentials: "include",
    headers: companyId ? { "x-company-id": companyId } : {},
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { error?: string } | null;
    throw new Error(body?.error ?? response.statusText);
  }
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `CASCENA-Monthly-Finance-${month}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** Finance reports with company-admin baseline controls. All recognition and residual-cost figures come from CASCENA. */
export function MonthlyFinanceReport({ readOnly = false, financeOrigin = {} }: { readOnly?: boolean; financeOrigin?: FinanceOrigin }) {
  const canExport = useFinanceFunction("pnl.export_excel");
  const user = useAuth(state => state.user);
  // Match the surrounding P&L page's company-admin boundary, including when
  // auth changes while an old Finance report tab is still selected.
  const canViewFinanceReport = user?.role === "company_admin";
  const availableTabs = reportTabs.filter(([value]) => value !== "finance" || canViewFinanceReport);
  const returnCompany = selectedCompanyId() ?? user?.companyId ?? null;
  const returning = canReturnToFinance(financeOrigin, returnCompany);
  const returnTab = financeOrigin.financeTab === "matrix" ? "matrix" : "carry";
  const initialTab = returning ? returnTab : canViewFinanceReport ? "finance" : "matrix";
  const [selectedTab, setTab] = useState<"finance" | "matrix" | "carry">(initialTab);
  const tab = selectedTab === "finance" && !canViewFinanceReport ? "matrix" : selectedTab;
  const [matrixOpened, setMatrixOpened] = useState(initialTab === "matrix");
  const [carryOpened, setCarryOpened] = useState(initialTab === "carry");
  useEffect(() => { if (returning) { setTab(returnTab); if (returnTab === "matrix") setMatrixOpened(true); else setCarryOpened(true); } }, [returning, returnTab, financeOrigin.financeMonth, financeOrigin.financeView]);
  const companyId = selectedCompanyId();
  const queryClient = useQueryClient();
  const { isMobileViewport } = useMobileUiV1State();
  const [month, setMonth] = useState(defaultFinanceMonth);
  const latestMonth = defaultFinanceMonth();
  const [baselineDraft, setBaselineDraft] = useState<{ companyId: string | null; value: string } | null>(null);
  const settings = useQuery({
    queryKey: ["eex-monthly-finance-settings", companyId],
    enabled: canViewFinanceReport,
    // A baseline may be changed by another admin or tab; check it on return.
    staleTime: 0,
    refetchOnWindowFocus: true,
    queryFn: ({ signal }) => apiContract(FinanceReportSettingsSchema, "/eex/recognition/finance-report/settings", {
      signal, headers: companyId ? { "x-company-id": companyId } : {},
    }),
    retry: false,
  });
  const baselineValue = baselineDraft?.companyId === companyId ? baselineDraft.value : settings.data?.baselineMonth ?? "";
  let validBaseline = true;
  try { financeBaselineMonth(baselineValue); } catch { validBaseline = false; }
  const validMonth = /^\d{4}-(0[1-9]|1[0-2])$/.test(month) && month >= "2000-01" && month <= latestMonth;
  const report = useQuery({
    queryKey: ["eex-monthly-finance-report", companyId, month],
    queryFn: async ({ signal }) => {
      const data = await apiContract(MonthlyFinanceReportSchema, financeReportPath(month), {
        signal,
        headers: companyId ? { "x-company-id": companyId } : {},
      });
      if (data.month !== month || (companyId && data.companyId !== companyId)) {
        throw new Error("The returned report does not match the selected company and month.");
      }
      return data;
    },
    // A report replays the chosen month; load only on an explicit request.
    enabled: false,
    retry: false,
  });
  const download = useMutation({
    mutationFn: ({ selectedMonth, selectedCompany }: { selectedMonth: string; selectedCompany: string | null }) =>
      downloadReport(selectedMonth, selectedCompany),
  });
  const saveBaseline = useMutation({
    mutationFn: ({ baselineMonth, companyId: selectedCompany }: { baselineMonth: string | null; companyId: string | null }) =>
      apiContract(FinanceReportSettingsSchema, "/eex/recognition/finance-report/settings", {
        method: "PUT", body: JSON.stringify({ baselineMonth }),
        headers: selectedCompany ? { "x-company-id": selectedCompany } : {},
      }),
    onSuccess: async (saved, variables) => {
      queryClient.setQueryData(["eex-monthly-finance-settings", variables.companyId], saved);
      const displayedCompany = variables.companyId === selectedCompanyId();
      const reloadDisplayedReport = displayedCompany && !!report.data;
      await queryClient.resetQueries({ queryKey: ["eex-monthly-finance-report", variables.companyId] });
      if (displayedCompany) {
        setBaselineDraft(null);
        download.reset();
        if (reloadDisplayedReport) await report.refetch();
      }
    },
  });
  const baselineMutationVisible = saveBaseline.variables?.companyId === companyId;
  const baselineReportStale = !!report.data && !!settings.data && !financeReportMatchesBaseline(report.data, settings.data.baselineMonth);

  return (
    <section aria-labelledby="monthly-finance-report-title" className={cn("finance-workspace", tab === "matrix" && "finance-workspace-compact")}>
      <header className="fin-workspace-heading">
        <div><p className="fin-eyebrow">CASCENA / Finance</p>
          <h2 id="monthly-finance-report-title" className="fin-workspace-title">Monthly finance report</h2>
          <p className="fin-copy">Recognized P&L, unmatched positions and carry-forward, with statement VM reconciliation.</p>
        </div><span className="fin-brand-mark" aria-hidden="true"><ChartNoAxesCombined size={22} strokeWidth={1.5} /></span>
      </header>
      <div role="tablist" aria-label="Finance report views" className="fin-tabs">
        {availableTabs.map(([value, title], index) => <button
          key={value} id={`finance-tab-${value}`} type="button" role="tab" aria-selected={tab === value}
          aria-controls={`finance-panel-${value}`} tabIndex={tab === value ? 0 : -1}
          onClick={() => { setTab(value); if (value === "matrix") setMatrixOpened(true); if (value === "carry") setCarryOpened(true); }}
          onKeyDown={(event) => {
            if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
            event.preventDefault();
            const next = availableTabs[event.key === "Home" ? 0 : event.key === "End" ? availableTabs.length - 1 : (index + (event.key === "ArrowRight" ? 1 : -1) + availableTabs.length) % availableTabs.length]![0];
            setTab(next); if (next === "matrix") setMatrixOpened(true); if (next === "carry") setCarryOpened(true);
            document.getElementById(`finance-tab-${next}`)?.focus();
          }}
          className="fin-tab"
        >{title}</button>)}
      </div>
      {canViewFinanceReport && <div id="finance-panel-finance" role="tabpanel" aria-labelledby="finance-tab-finance" hidden={tab !== "finance"} className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <form className="flex flex-wrap items-end gap-2" onSubmit={(event) => {
          event.preventDefault();
          if (validMonth && !report.isFetching && !saveBaseline.isPending) void report.refetch();
        }}>
          <label className="flex flex-col gap-1 text-xs text-muted">
            Financial month
            <input
              aria-label="Financial month"
              type="month"
              min="2000-01"
              max={latestMonth}
              required
              disabled={saveBaseline.isPending}
              value={month}
              onChange={(event) => { setMonth(event.target.value); download.reset(); }}
              className="min-h-11 rounded border border-border bg-bg px-2 py-1 text-sm text-fg sm:min-h-9"
            />
          </label>
          <button
            type="submit"
            disabled={!validMonth || report.isFetching || saveBaseline.isPending}
            className="min-h-11 rounded border border-border px-3 py-2 text-xs font-medium hover:bg-border/30 disabled:opacity-50 sm:min-h-9"
          >
            {report.isFetching ? "Loading report…" : report.data ? "Refresh report" : "Load report"}
          </button>
          {canExport && (!isMobileViewport || readOnly) && report.data && !baselineReportStale && (
            <button
              type="button"
              disabled={download.isPending || report.isFetching || saveBaseline.isPending || settings.isFetching}
              onClick={() => download.mutate({ selectedMonth: month, selectedCompany: companyId })}
              className="min-h-9 rounded border border-border px-3 py-2 text-xs font-medium hover:bg-border/30 disabled:opacity-50"
            >{download.isPending ? "Exporting…" : "Export Excel"}</button>
          )}
        </form>
      </div>
      {readOnly ? <p className="text-xs text-muted">Reporting baseline: {settings.data?.baselineMonth ?? "Not set"}. Managed by your company administrator.</p> : <form className="space-y-2 rounded border border-border bg-border/10 p-3" onSubmit={(event) => {
        event.preventDefault();
        if (validBaseline && settings.data && !saveBaseline.isPending) {
          saveBaseline.mutate({ baselineMonth: financeBaselineMonth(baselineValue), companyId });
        }
      }}>
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-muted">
            Reporting baseline
            <input aria-label="Reporting baseline" type="month" min="2000-01" max={latestMonth}
              value={baselineValue} disabled={settings.isPending || saveBaseline.isPending}
              onChange={(event) => { setBaselineDraft({ companyId, value: event.target.value }); saveBaseline.reset(); }}
              className="min-h-11 rounded border border-border bg-bg px-2 py-1 text-sm text-fg sm:min-h-9" />
          </label>
          <button type="submit" disabled={!settings.data || !validBaseline || saveBaseline.isPending}
            className="min-h-11 rounded border border-border px-3 py-2 text-xs font-medium hover:bg-border/30 disabled:opacity-50 sm:min-h-9">
            {saveBaseline.isPending ? "Saving baseline…" : "Save baseline"}
          </button>
          {baselineValue && <button type="button" disabled={saveBaseline.isPending}
            onClick={() => { setBaselineDraft({ companyId, value: "" }); saveBaseline.reset(); }}
            className="min-h-11 rounded px-3 py-2 text-xs text-muted hover:bg-border/30 sm:min-h-9">Clear baseline</button>}
        </div>
        <p className="text-xs text-muted">The portfolio result starts at zero on the first day of this month. Positions retain their CASCENA acquisition costs. Leave the month blank and save to remove the baseline.</p>
        {settings.data?.baselineMonth ? <p className="text-xs text-muted">Saved baseline: {settings.data.baselineMonth}-01 · initial portfolio result 0.00.</p>
          : settings.data && <p className="text-xs text-muted">No reporting baseline is set for this company.</p>}
        {!validBaseline && <p role="alert" className="text-xs text-negRed">Choose a completed reporting baseline month from January 2000 onward.</p>}
        {settings.error && <p role="alert" className="text-xs text-negRed">Baseline settings could not be loaded: {errMessage(settings.error)}</p>}
        {baselineMutationVisible && saveBaseline.error && <p role="alert" className="text-xs text-negRed">Baseline could not be saved: {errMessage(saveBaseline.error)}</p>}
        {baselineMutationVisible && saveBaseline.isSuccess && <p role="status" className="text-xs text-posGreen">{saveBaseline.data.baselineMonth
          ? `Reporting baseline saved: ${saveBaseline.data.baselineMonth}-01. Portfolio result starts at 0.00.` : "Reporting baseline cleared."}</p>}
      </form>}
      {report.error && <p role="alert" className="text-xs text-negRed">Report could not be loaded: {errMessage(report.error)}</p>}
      {baselineReportStale && !saveBaseline.isPending && <p role="alert" className="text-xs text-warnOrange">The saved reporting baseline has changed. Refresh the report to update its figures and Excel export.</p>}
      {download.error && <p role="alert" className="text-xs text-negRed">Export failed: {errMessage(download.error)}</p>}
      {report.isFetching && <p role="status" className="text-xs text-muted">Preparing {month} from CASCENA’s recognition and position records…</p>}
      {!report.data && !report.isFetching && !report.error && <p className="text-xs text-muted">Choose a completed financial month and load its report.</p>}
      {report.data && !baselineReportStale && <ReportContent data={report.data} baselineMonth={settings.data?.baselineMonth} />}
      </div>}
      <div id="finance-panel-matrix" role="tabpanel" aria-labelledby="finance-tab-matrix" hidden={tab !== "matrix"}>
        {(matrixOpened || tab === "matrix") && <PortfolioMatrixWorkspace key={`${returnCompany ?? "current-company"}:${financeOrigin.financeTab === "matrix" ? financeOrigin.financeView ?? "matrix" : ""}`} active={tab === "matrix"} financeOrigin={financeOrigin} />}
      </div>
      <div id="finance-panel-carry" role="tabpanel" aria-labelledby="finance-tab-carry" hidden={tab !== "carry"}>
        {carryOpened && <FinanceCarryRollforward key={`${returnCompany ?? "current-company"}:${financeOrigin.financeView ?? financeOrigin.financeMonth ?? ""}`} financeOrigin={financeOrigin} />}
      </div>
    </section>
  );
}

function ReportContent({ data, baselineMonth }: { data: MonthlyFinanceReportData; baselineMonth?: string | null }) {
  const recognition = data.recognition;
  return (
    <div className="space-y-3" aria-label={`Finance report for ${data.month}`}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
        <span className="font-medium text-fg">{data.month}</span>
        {data.source.periodStatus === "draft_month" && <span className="rounded bg-warnOrange/10 px-2 py-1 text-warnOrange">Draft month</span>}
        <span className={cn("rounded px-2 py-1", recognition.approvalComplete ? "bg-posGreen/10 text-posGreen" : "bg-warnOrange/10 text-warnOrange")}>
          {recognition.approvalComplete ? "Approved recognition complete" : "Recognition approval incomplete"}
        </span>
        <span>Generated {new Date(data.generatedAt).toLocaleString()} · source as of {data.source.asOf}</span>
      </div>
      <p className="text-xs text-muted">Opening and closing carry are reconstructed using current allocations. Approved P&L retains its frozen month-close snapshots.</p>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Summary label="Approved P&L · EUR" value={recognition.approvedTotalEur}>
          {recognition.approvalComplete ? "Frozen hard-close snapshots" : `Approved subtotal: ${amount(recognition.approvedSubtotalEur)} EUR`}
        </Summary>
        <Summary label="Current-policy P&L · EUR" value={recognition.liveTotalEur}>
          Close {amount(recognition.closeEur)} · Delivery {amount(recognition.deliveryEur)}
          {recognition.roundingAdjustmentEur !== 0 && <> · Rounding {amount(recognition.roundingAdjustmentEur)}</>}
        </Summary>
        {data.carryTotalsByCurrency.map((total) => (
          <Summary key={total.currency} label={`Closing carry · ${total.currency}`} value={total.closing}>
            Opening carry {amount(total.opening)} {total.currency}
          </Summary>
        ))}
        {data.vm.totalsByCurrency.length > 0 ? data.vm.totalsByCurrency.map((total) => (
          <Summary key={`vm-${total.currency}`} label={`Statement VM · ${total.currency}`} value={total.amount}>
            {total.statementCount} statement(s) · report-date basis
          </Summary>
        )) : <Summary label="Statement VM" value={null}>No statements in this month</Summary>}
      </div>

      {data.baseline ? (
        <section aria-label={`Portfolio result since ${data.baseline.startDate}`} className="space-y-3 rounded border border-border p-3">
          <h3 className="text-sm font-medium">Portfolio result since {data.baseline.startDate}</h3>
          <p className="text-xs text-muted">Initial portfolio result: {amount(data.baseline.initialValue)} · through {data.baseline.endDate}. Opening positions and their acquisition costs remain in CASCENA.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {data.baseline.totalsByCurrency.map((total) => <Summary key={total.currency} label={`Portfolio result · ${total.currency}`} value={total.portfolioResult}>
              Cumulative result from the reporting baseline
            </Summary>)}
          </div>
          <ScrollTable name="Portfolio result since baseline detail">
            <thead><tr className="border-b border-border text-muted"><Th>Currency</Th><Th className="text-right">Recognized P&L since baseline</Th><Th className="text-right">Closing carry</Th><Th className="text-right">Opening carry at baseline</Th><Th className="text-right">+ Opening adjustment</Th><Th className="text-right">− Reclassification timing adjustment</Th>{data.baseline.totalsByCurrency.some((total) => total.roundingAdjustment !== 0) && <Th className="text-right">+ Rounding adjustment</Th>}<Th className="text-right">Portfolio result</Th></tr></thead>
            <tbody>{data.baseline.totalsByCurrency.map((total) => <tr key={total.currency} className="border-b border-border/50">
              <Td>{total.currency}</Td><Money value={total.recognizedPnl} /><Money value={total.closingCarry} /><Money value={total.openingCarry} /><Money value={total.openingAdjustment} /><Money value={total.reclassTimingAdjustment} />{data.baseline!.totalsByCurrency.some((row) => row.roundingAdjustment !== 0) && <Money value={total.roundingAdjustment} />}<Money value={total.portfolioResult} />
            </tr>)}</tbody>
          </ScrollTable>
          <p className="text-xs text-muted">{data.baseline.basis}</p>
        </section>
      ) : <p className="text-xs text-muted">{baselineMonth && data.month < baselineMonth
        ? `The reporting baseline starts on ${baselineMonth}-01 and does not apply to this earlier month.`
        : baselineMonth ? "The baseline result is not included in this report. Refresh the report to load it."
          : "Set a reporting baseline to show the cumulative portfolio result starting from zero."}</p>}

      <Detail title={`Recognized P&L · ${recognition.liveLines.length} current-policy lines`}>
        <p className="text-xs text-muted">Approved amounts come from hard-close snapshots. Current-policy amounts use the application’s recognition rules. Estimates do not enter recognized totals.</p>
        {recognition.cells.length === 0 ? <p className="text-xs text-muted">No recognition cells for this month.</p> : (
          <ScrollTable name="Recognition approval by book and trader">
            <thead><tr className="border-b border-border text-muted"><Th>Book</Th><Th>Trader</Th><Th>State</Th><Th className="text-right">Current EUR</Th><Th className="text-right">Approved EUR</Th><Th className="text-right">Difference EUR</Th><Th>Approved at</Th><Th>Snapshot</Th></tr></thead>
            <tbody>{recognition.cells.map((cell, index) => (
              <tr key={index} className="border-b border-border/50"><Td>{label(cell.book)}</Td><Td>{label(cell.trader)}</Td><Td>{cell.state}</Td><Money value={cell.liveEur} /><Money value={cell.approvedEur} /><Money value={cell.differenceEur} /><Td>{label(cell.approvedAt)}</Td><Td>{label(cell.snapshotId)}</Td></tr>
            ))}</tbody>
          </ScrollTable>
        )}
        {recognition.approvedLines.length > 0 && <RecognitionLines name="Approved recognition detail" lines={recognition.approvedLines} />}
        {recognition.liveLines.length > 0 && <RecognitionLines name="Current-policy recognition detail" lines={recognition.liveLines} />}
      </Detail>

      <Detail title={`Unmatched positions and carry-forward · ${data.carry.rows.length} rows`}>
        <p className="text-xs text-muted">Opening {data.carry.openingDate} · Closing {data.carry.closingDate}. Residual cost is CASCENA’s acquisition cost of the unmatched position. Amounts remain in each contract’s currency. Buys and sells show executions; cascades and deliveries can also change the closing quantity. Labelled PAIRs can span brokers and venues.</p>
        {data.carry.rows.length === 0 ? <p className="text-xs text-muted">No unmatched opening or closing positions.</p> : (
          <ScrollTable name="Unmatched positions and carry-forward detail">
            <thead><tr className="border-b border-border text-muted"><Th>Contract</Th><Th>Book · Trader</Th><Th>PAIR</Th><Th>Broker</Th><Th>Venue</Th><Th>Side</Th><Th>Price unit</Th><Th className="text-right">Opening lots</Th><Th className="text-right">Opening cost price</Th><Th className="text-right">Opening carry</Th><Th className="text-right">Buy lots</Th><Th className="text-right">Sell lots</Th><Th className="text-right">Closing lots</Th><Th className="text-right">Closing volume</Th><Th className="text-right">Residual cost price</Th><Th className="text-right">Residual cost</Th><Th className="text-right">Closing mark</Th><Th>Mark date</Th><Th className="text-right">Closing carry</Th></tr></thead>
            <tbody>{data.carry.rows.map((row) => (
              <tr key={row.key} className="border-b border-border/50"><Td><Contract value={row.contract} /></Td><Td>{label(row.book)} · {label(row.trader)}</Td><Td>{label(row.pair)}</Td><Td>{row.pair ? "PAIR group" : label(row.broker)}</Td><Td>{row.pair ? "PAIR group" : label(row.venue)}</Td><Td>{row.side === "B" ? "Long" : "Short"}</Td><Td>{row.priceUnit}</Td><Td className="text-right tabular-nums">{price.format(row.opening.lots)}</Td><Price value={row.opening.avgAcquisitionPrice} /><Money value={row.opening.carryAmount} /><Td className="text-right tabular-nums">{price.format(row.tradeBuyLots)}</Td><Td className="text-right tabular-nums">{price.format(row.tradeSellLots)}</Td><Td className="text-right tabular-nums">{price.format(row.closing.lots)}</Td><Td className="text-right tabular-nums">{price.format(row.closing.volume)} {row.unit}</Td><Price value={row.closing.avgAcquisitionPrice} /><Money value={row.closing.acquisitionCost} /><Price value={row.closing.markPrice} /><Td>{label(row.closing.markDate)}</Td><Money value={row.closing.carryAmount} /></tr>
            ))}</tbody>
          </ScrollTable>
        )}
      </Detail>

      <Detail title={`VM reconciliation · ${data.vm.statements.length} statements`}>
        <p className="text-xs text-muted">Variation margin is cash settlement. Its timing can differ from recognized P&L. Statement coverage follows report dates in the selected month. Fees are disclosed separately from gross recognized P&L.</p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Summary label="Computed VM · EUR" value={data.vm.computedEur}>CASCENA’s variation margin ledger</Summary>
          <Summary label="Statement VM · EUR" value={data.vm.eurTotal}>Imported statement report dates</Summary>
          <Summary label="VM difference · EUR" value={data.vm.differenceEur}>Computed minus statement VM</Summary>
        </div>
        {data.vm.days.length > 0 && (
          <ScrollTable name="Daily variation margin reconciliation in EUR">
            <thead><tr className="border-b border-border text-muted"><Th>Report date</Th><Th className="text-right">Computed EUR</Th><Th className="text-right">Statement EUR</Th><Th className="text-right">Difference EUR</Th></tr></thead>
            <tbody>{data.vm.days.map((day, index) => (
              <tr key={`${day.date}-${index}`} className="border-b border-border/50"><Td>{day.date}</Td><Money value={day.computedIncomplete ? null : day.computedTotal} /><Money value={day.bankTotal} /><Money value={day.diff} /></tr>
            ))}</tbody>
          </ScrollTable>
        )}
        {data.vm.statements.length === 0 ? <p className="text-xs text-muted">No imported statements in this month.</p> : (
          <ScrollTable name="Monthly variation margin statements">
            <thead><tr className="border-b border-border text-muted"><Th>Report date</Th><Th>Payment date</Th><Th>Currency</Th><Th className="text-right">Statement VM</Th><Th className="text-right">Fees</Th><Th>Statement</Th></tr></thead>
            <tbody>{data.vm.statements.map((statement) => (
              <tr key={statement.id} className="border-b border-border/50"><Td>{statement.reportDate}</Td><Td>{label(statement.paymentDate)}</Td><Td>{statement.currency}</Td><Money value={statement.variationMargin} /><Money value={statement.fees} /><Td>{statement.id}</Td></tr>
            ))}</tbody>
          </ScrollTable>
        )}
        {data.vm.notes.length > 0 && <ul className="list-disc space-y-1 pl-4 text-xs text-muted">{data.vm.notes.map((note, index) => <li key={index}>{note}</li>)}</ul>}
      </Detail>

      <Detail title="P&L and carry bridge · native EUR">
        <p className="text-xs text-muted">{data.bridge.basis}</p>
        <ScrollTable name="P&L and carry reconciliation in native EUR">
          <thead><tr className="border-b border-border text-muted"><Th>Component</Th><Th className="text-right">EUR</Th></tr></thead>
          <tbody>
            <tr><Td>Opening carry</Td><Money value={data.bridge.openingCarry} /></tr>
            <tr><Td>+ Computed VM</Td><Money value={data.bridge.computedVm} /></tr>
            <tr><Td>− Recognized P&L</Td><Money value={data.bridge.recognizedPnl} /></tr>
            <tr><Td>+ Reclassification timing adjustment</Td><Money value={data.bridge.reclassTimingAdjustment} /></tr>
            <tr><Td>− Closing carry</Td><Money value={data.bridge.closingCarry} /></tr>
            <tr className="border-t border-border font-medium"><Td>Difference</Td><Money value={data.bridge.difference} /></tr>
          </tbody>
        </ScrollTable>
        <p className="text-xs text-muted">{data.bridge.monthCoverageComplete ? "Imported statements cover the month’s ECC business dates." : "Statement coverage for the full month is incomplete."}</p>
        {data.bridge.absentStatementDates.length > 0 && <p className="text-xs text-muted">Dates without an imported statement: {data.bridge.absentStatementDates.join(", ")}.</p>}
      </Detail>

      <Detail title="Report checks and scope">
        <ul className="space-y-2 text-xs">{data.checks.map((check, index) => (
          <li key={index}><span className={cn("mr-2 font-medium", check.status === "pass" ? "text-posGreen" : "text-warnOrange")}>{check.status === "pass" ? "Pass" : "Review"}</span><span className="font-medium">{check.name}</span> · {check.detail}{check.difference !== null && <> · Difference {amount(check.difference)}</>}</li>
        ))}</ul>
        <ul className="list-disc space-y-1 pl-4 text-xs text-muted">{data.scopeNotes.map((note, index) => <li key={index}>{note}</li>)}</ul>
      </Detail>
    </div>
  );
}

function RecognitionLines({ name, lines }: { name: string; lines: MonthlyFinanceReportData["recognition"]["liveLines"] }) {
  return (
    <div className="space-y-1">
      <h3 className="text-xs font-medium">{name}</h3>
      <ScrollTable name={name}>
        <thead><tr className="border-b border-border text-muted"><Th>Contract</Th><Th>Book · Trader</Th><Th>PAIR</Th><Th>Recognition</Th><Th>Recognition date</Th><Th>Actual close date</Th><Th>Finality</Th><Th>Price unit</Th><Th className="text-right">Entry price</Th><Th className="text-right">Exit price</Th><Th className="text-right">Volume</Th><Th className="text-right">Native P&L</Th><Th className="text-right">Recognized EUR</Th><Th>Entry trade</Th><Th>Exit trade</Th><Th>Reclassification</Th><Th>Recognition schedule</Th></tr></thead>
        <tbody>{lines.map((line, index) => (
          <tr key={index} className="border-b border-border/50"><Td><Contract value={line.contract} /></Td><Td>{label(line.book)} · {label(line.trader)}</Td><Td>{label(line.pair)}</Td><Td>{line.exitKind}</Td><Td>{line.recognitionDate}</Td><Td>{line.exitKind === "close" ? line.exitDate ?? line.schedule?.sourceDate ?? "—" : "—"}</Td><Td>{line.finality}</Td><Td>{line.priceUnit}</Td><Td className="text-right tabular-nums">{price.format(line.entry)}</Td><Td className="text-right tabular-nums">{price.format(line.exit)}</Td><Td className="text-right tabular-nums">{price.format(line.volume)} {line.unit}</Td><Money value={line.nativeAmount} /><Money value={line.recognizedEUR} /><Td>{label(line.entryTradeId)}</Td><Td>{label(line.exitTradeId)}</Td><Td>{line.reclass ? `${line.reclass.name || line.reclass.id} · from ${line.reclass.fromMonth}` : "—"}</Td><Td>{line.schedule ? <span title={line.schedule.reason}>{line.schedule.remainder ? "Automatic remainder" : `${line.schedule.percent}% allocation`} · from {line.schedule.sourceMonth} · {line.schedule.id}</span> : "—"}</Td></tr>
        ))}</tbody>
      </ScrollTable>
    </div>
  );
}

function Summary({ label: title, value, children }: { label: string; value: number | null; children: ReactNode }) {
  return <div className="rounded border border-border px-3 py-2"><p className="text-xs text-muted">{title}</p><p className="mt-1 text-lg font-medium tabular-nums">{amount(value)}</p><p className="mt-1 text-[11px] text-muted">{children}</p></div>;
}

function Money({ value }: { value: number | null }) {
  return <Td className="text-right tabular-nums">{amount(value)}</Td>;
}

function Price({ value }: { value: number | null }) {
  return <Td className="text-right tabular-nums">{value === null ? "—" : price.format(value)}</Td>;
}
